/**
 * NPI - Complementary Products
 *
 * Owns its own recommendations fetch (intent=complementary via the Search &
 * Discovery backend) so it does not depend on Dawn's ProductRecommendations.
 *
 * Checking a row's box adds that variant to the cart, unchecking removes its
 * line. Cart drawer contents and the icon bubble are refreshed in place, but
 * the drawer is never opened -- toggling a checkbox should not take over the page.
 */
if (!customElements.get('npi-complementary-products')) {
  customElements.define(
    'npi-complementary-products',
    class NpiComplementaryProducts extends HTMLElement {
      connectedCallback() {
        this.lines = new Map();
        this.busy = new Set();

        this.addEventListener('change', this.onChange);
        this.addEventListener('click', this.onClick);

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source === 'npi-complementary-products') return;
          this.syncFromCart();
        });

        // Already populated when rendered through the Section Rendering API.
        if (this.querySelector('.npi-complementary__card')) {
          this.activate();
        } else {
          this.observe();
        }
      }

      disconnectedCallback() {
        this.cartUpdateUnsubscriber?.();
        this.observer?.disconnect();
      }

      /* ----------------------------------------------------------- loading */

      observe() {
        this.observer = new IntersectionObserver(
          (entries, observer) => {
            if (!entries[0].isIntersecting) return;
            observer.unobserve(this);
            this.load();
          },
          { rootMargin: '0px 0px 400px 0px' }
        );
        this.observer.observe(this);
      }

      load() {
        const url = `${this.dataset.url}&product_id=${this.dataset.productId}&section_id=${this.dataset.sectionId}`;

        fetch(url)
          .then((response) => response.text())
          .then((text) => {
            const source = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('npi-complementary-products');

            // No complementary products set up for this product -- take up no space.
            if (!source?.querySelector('.npi-complementary__card')) {
              this.remove();
              return;
            }

            this.innerHTML = source.innerHTML;
            this.activate();
          })
          .catch((error) => {
            console.error(error);
            this.remove();
          });
      }

      activate() {
        this.errorElement = this.querySelector('.npi-complementary__error');
        this.classList.add('npi-complementary--loaded');
        this.syncFromCart();
      }

      /* ---------------------------------------------------------- elements */

      get inputs() {
        return Array.from(this.querySelectorAll('.npi-complementary__check-input'));
      }

      get cartElement() {
        return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      }

      get sectionIds() {
        const cart = this.cartElement;
        return typeof cart?.getSectionsToRender === 'function'
          ? cart.getSectionsToRender().map((section) => section.id)
          : ['cart-drawer', 'cart-icon-bubble'];
      }

      /* ------------------------------------------------------------ events */

      onClick(event) {
        const toggle = event.target.closest('.npi-complementary__toggle');
        if (!toggle || !this.contains(toggle)) return;

        const panel = document.getElementById(toggle.getAttribute('aria-controls'));
        if (!panel) return;

        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!expanded));
        panel.hidden = expanded;
      }

      onChange(event) {
        const input = event.target;
        if (!input.classList?.contains('npi-complementary__check-input')) return;
        if (this.busy.has(input.dataset.variantId)) return;

        this.clearError();
        input.checked ? this.addVariant(input) : this.removeVariant(input);
      }

      /* -------------------------------------------------------------- cart */

      addVariant(input) {
        const variantId = input.dataset.variantId;
        const body = JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: this.sectionIds,
          sections_url: window.location.pathname,
        });

        this.setBusy(input, true);

        return this.request(routes.cart_add_url, body, input, variantId).finally(() =>
          this.setBusy(input, false)
        );
      }

      removeVariant(input) {
        const variantId = input.dataset.variantId;
        const line = this.lines.get(variantId);

        // Line unknown (cart changed in another tab) -- resync rather than guess.
        if (!line) return this.syncFromCart();

        const body = JSON.stringify({
          id: line.key,
          quantity: 0,
          sections: this.sectionIds,
          sections_url: window.location.pathname,
        });

        this.setBusy(input, true);

        return this.request(routes.cart_change_url, body, input, variantId).finally(() =>
          this.setBusy(input, false)
        );
      }

      request(url, body, input, variantId) {
        return fetch(url, { ...fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then((state) => {
            if (state.status) throw new Error(state.description || state.message);

            this.renderCartSections(state.sections);

            return publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'npi-complementary-products',
              productVariantId: variantId,
              cartData: state,
            }).then(() => this.syncFromCart());
          })
          .catch((error) => {
            input.checked = !input.checked;
            this.showError(error.message);
          });
      }

      syncFromCart() {
        return fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => response.json())
          .then((cart) => {
            this.lines.clear();
            cart.items.forEach((item) => {
              this.lines.set(String(item.variant_id), { key: item.key, quantity: item.quantity });
            });

            this.inputs.forEach((input) => {
              if (this.busy.has(input.dataset.variantId)) return;
              input.checked = this.lines.has(input.dataset.variantId);
            });

            this.updateCounts();
          })
          .catch(() => {});
      }

      renderCartSections(sections) {
        const cart = this.cartElement;
        if (!sections || typeof cart?.getSectionsToRender !== 'function') return;

        cart.getSectionsToRender().forEach((section) => {
          const element = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          const html = sections[section.id];
          if (!element || !html) return;

          const source = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector(section.selector || '.shopify-section');
          if (source) element.innerHTML = source.innerHTML;
        });

        cart.classList.remove('is-empty');
        cart.querySelector('.drawer__inner')?.classList.remove('is-empty');
      }

      /* ---------------------------------------------------------------- ui */

      /**
       * Surfaces how many variants of a collapsed product are in the cart, so
       * the selection stays visible when the accordion is closed.
       */
      updateCounts() {
        this.querySelectorAll('.npi-complementary__toggle').forEach((toggle) => {
          const panel = document.getElementById(toggle.getAttribute('aria-controls'));
          const badge = toggle.querySelector('.npi-complementary__count');
          if (!panel || !badge) return;

          const count = panel.querySelectorAll('.npi-complementary__check-input:checked').length;
          badge.textContent = count;
          badge.hidden = count === 0;
        });
      }

      setBusy(input, busy) {
        const variantId = input.dataset.variantId;
        busy ? this.busy.add(variantId) : this.busy.delete(variantId);
        input
          .closest('.npi-complementary__check')
          ?.classList.toggle('npi-complementary__check--loading', busy);
        input.disabled = busy;
      }

      showError(message) {
        if (!this.errorElement) return;
        this.errorElement.textContent = message || window.cartStrings?.error || '';
        this.errorElement.hidden = !this.errorElement.textContent;
      }

      clearError() {
        if (!this.errorElement) return;
        this.errorElement.textContent = '';
        this.errorElement.hidden = true;
      }
    }
  );
}
